(() => {
  if (customElements.get('pnp-dispatch')) return;
  const requests = new Map();
  const dayMs = 24 * 60 * 60 * 1000;
  const maxEstimateAgeDays = 30;
  function deliveryData(url) {
    if (!requests.has(url)) {
      requests.set(url, fetch(url).then(response => {
        if (!response.ok) throw new Error('Delivery estimates unavailable');
        return response.json();
      }).catch(error => { requests.delete(url); throw error; }));
    }
    return requests.get(url);
  }
  class PnpDispatch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      try { this.config = JSON.parse(this.querySelector('[data-dispatch-config]').textContent); }
      catch (_) { return; }
      this.initialized = true;
      this.scope = this.closest('.product__info-container') || this.closest('.shopify-section') || this.parentElement;
      this.onChange = () => this.update(this.selectedVariant());
      this.onCountry = () => this.renderDelivery();
      this.country = this.querySelector('[data-delivery-country]');
      this.region = this.querySelector('[data-delivery-region]');
      this.scope.addEventListener('change', this.onChange);
      this.country.addEventListener('change', this.onCountry);
      this.region.addEventListener('change', this.onCountry);
      this.update(this.selectedVariant());
      deliveryData(this.dataset.deliveryUrl).then(data => {
        this.delivery = data;
        this.renderDelivery();
      }).catch(() => {
        this.showDeliveryFallback(this.config.strings.failed, 'failed');
      });
    }
    disconnectedCallback() {
      this.scope?.removeEventListener('change', this.onChange);
      this.country?.removeEventListener('change', this.onCountry);
      this.region?.removeEventListener('change', this.onCountry);
      this.initialized = false;
    }
    selectedVariant() {
      const picker = this.scope.querySelector('variant-radios, variant-selects');
      if (picker) {
        let groups = Array.from(picker.querySelectorAll('.product-form__input'));
        if (!groups.length) groups = Array.from(picker.querySelectorAll('fieldset, select')).filter(group => group.tagName === 'FIELDSET' || !group.closest('fieldset'));
        const options = groups.map(group => {
          if (group.tagName === 'SELECT') return group.value;
          const select = group.querySelector('select');
          return select ? select.value : group.querySelector('input[type="radio"]:checked')?.value;
        });
        if (options.length && options.every(value => value !== undefined) && options.length === this.config.variants[0]?.options.length) {
          return this.config.variants.find(variant => variant.options.every((value, index) => String(value) === String(options[index]))) || null;
        }
      }
      const id = this.scope.querySelector('form[action*="/cart/add"] [name="id"]')?.value || this.config.initialId;
      return this.config.variants.find(variant => String(variant.id) === String(id)) || null;
    }
    update(variant) {
      const strings = this.config.strings;
      const available = !!variant?.available;
      this.querySelector('[data-dispatch-stock]').textContent = available ? strings.inStock : strings.soldOut;
      this.querySelector('[data-dispatch-title]').textContent = available ? strings.nextDay : strings.unavailable;
      this.querySelector('[data-dispatch-body]').textContent = available ? strings.cutoff : strings.unavailableBody;
      const message = strings.message.replace('__PRODUCT__', this.config.productTitle).replace('__OPTION__', variant ? variant.options.join(' / ') : '');
      this.querySelector('[data-dispatch-contact]').href = 'sms:+61458658415' + (/iPhone|iPad|iPod/.test(navigator.userAgent) ? '&' : '?') + 'body=' + encodeURIComponent(message);
      this.dataset.state = available ? 'next_day' : 'unavailable';
      if (this.delivery) this.renderDelivery();
    }
    serviceText(service) {
      const strings = this.config.strings;
      if (service?.status === 'available' && service.text) return service.text;
      if (service?.status === 'suspended') return strings.suspended;
      return strings.notAvailable;
    }
    snapshotState() {
      const value = this.delivery?.checked;
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: null, fresh: false };
      const timestamp = Date.parse(value + 'T00:00:00Z');
      if (!Number.isFinite(timestamp)) return { date: null, fresh: false };
      const date = new Date(timestamp);
      if (date.toISOString().slice(0, 10) !== value) return { date: null, fresh: false };
      const ageDays = Math.floor(Date.now() / dayMs) - Math.floor(timestamp / dayMs);
      if (ageDays < 0) return { date: null, fresh: false };
      return { date, fresh: ageDays <= maxEstimateAgeDays };
    }
    showDeliveryFallback(message, state) {
      const loading = this.querySelector('[data-delivery-loading]');
      loading.hidden = false;
      loading.textContent = message;
      this.querySelector('[data-delivery-summary]').hidden = true;
      this.querySelector('[data-delivery-services]').hidden = true;
      for (const hook of ['summary-estimate', 'standard', 'express']) {
        this.querySelector('[data-delivery-' + hook + ']').textContent = '';
      }
      const notice = this.querySelector('[data-delivery-notice]');
      notice.hidden = true;
      notice.textContent = '';
      this.dataset.deliveryStatus = state;
    }
    renderDelivery() {
      if (!this.delivery) return;
      const strings = this.config.strings;
      const domestic = this.country.value === 'AU';
      const row = domestic ? this.delivery.domestic?.[this.region.value === 'WA' ? 'sameState' : this.region.value ? 'interstate' : 'general'] : this.delivery.countries?.[this.country.value];
      this.querySelector('[data-delivery-region-wrap]').hidden = !domestic;
      this.querySelector('[data-delivery-calculator]').hidden = !domestic;
      this.querySelector('[data-delivery-updates]').hidden = domestic;
      const source = domestic ? this.delivery.domestic?.source : this.delivery.source;
      if (source) this.querySelector('[data-delivery-source]').href = source;
      const snapshot = this.snapshotState();
      const checked = this.querySelector('[data-delivery-checked]');
      if (checked) {
        checked.hidden = !snapshot.date;
        const language = typeof document !== 'undefined' ? document.documentElement.lang : 'en-AU';
        const date = snapshot.date ? new Intl.DateTimeFormat(language || 'en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(snapshot.date) : '';
        checked.textContent = snapshot.date ? (strings.checked || 'Australia Post guide checked __DATE__').replace('__DATE__', date) : '';
      }
      if (!snapshot.fresh) {
        this.showDeliveryFallback(strings.stale || strings.unknown, 'stale');
        this.querySelector('[data-delivery-caption]').textContent = this.dataset.state === 'unavailable' ? strings.unavailableCaveat : domestic ? strings.domesticCaveat : strings.caveat;
        return;
      }
      const loading = this.querySelector('[data-delivery-loading]');
      loading.hidden = !!row;
      loading.textContent = strings.unknown;
      this.querySelector('[data-delivery-summary]').hidden = !row;
      this.querySelector('[data-delivery-services]').hidden = !row;
      this.dataset.deliveryStatus = row ? 'fresh' : 'unknown';
      if (row) {
        this.querySelector('[data-delivery-summary-label]').textContent = domestic ? strings.parcelPost : strings.standard;
        this.querySelector('[data-delivery-summary-estimate]').textContent = this.serviceText(row.standard);
        this.querySelector('[data-delivery-standard-label]').textContent = domestic ? strings.parcelPost : strings.standard;
        this.querySelector('[data-delivery-express-label]').textContent = domestic ? strings.expressPost : strings.express;
        this.querySelector('[data-delivery-standard]').textContent = this.serviceText(row.standard);
        this.querySelector('[data-delivery-express]').textContent = this.serviceText(row.express);
      }
      const notice = this.querySelector('[data-delivery-notice]');
      notice.hidden = !row?.notices;
      notice.textContent = row?.notices ? strings.notice : '';
      const noServices = row && row.standard?.status !== 'available' && row.express?.status !== 'available';
      this.querySelector('[data-delivery-caption]').textContent = this.dataset.state === 'unavailable' ? strings.unavailableCaveat : noServices ? strings.serviceUnavailable : domestic ? strings.domesticCaveat : strings.caveat;
    }
  }
  customElements.define('pnp-dispatch', PnpDispatch);
})();
